import Vue from "vue";
import VueRouter from "vue-router";
import constant from "../main.constants";
import fraudRoutes from "./fraud.routes";
import amlRoutes from "./aml.routes";
import aksitRoutes from "./aksit.routes";
import examRoutes from "./exam.routes";
import faceRoutes from "./face.routes";
import tutorialRoutes from "./tutorial.routes";
import cddRoutes from "./cdd.routes";
Vue.use(VueRouter);

import store from "../store";
import apicaller from "@/utils/apicaller";
import api from "@/main.api";
import commonFunc from "../common.func";
import { clearAllToasts } from "@/utils/toastCleaner";
import maintenanceService from "@/services/MaintenanceService";

const globalLoadingState = Vue.observable({
  isLoading: false
});

Vue.prototype.$globalLoading = globalLoadingState;

let otherRoutes = [
  {
    path: "/login",
    name: "Login",
    meta: { noSidebar: true },
    component: () => {
      // Furkan - Degiskenle kontrol edildiginde runtime'a düsecegi icin bu sekilde kontrol edildi.
      // Environment'a göre farklı login sayfaları yükleniyor, gerek görülürse separateLoaderEnvHelper.js'e taşınabilir.
      if (localStorage.getItem('OidcLoginOnly') === 'true') {
        return import("../views/auth/TkpayLogin.vue");
      }
      switch (process.env.VUE_APP_ENVIRONMENT) {
        case 'Jandarma':
          return import("../views/auth/JandarmaLogin.vue");
        case 'Gumruk':
          return import("../views/auth/GumrukLogin.vue");
        default:
          return import("../views/auth/Login.vue");
      }
    }
  },
  {
    path: "/forbidden",
    name: "Forbidden",
    component: () => import("../views/ErrorPage.vue"),
    props: { errorMessage: "forbiddenPage", errorCode: "403" }
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    meta: { noSidebar: true },
    component: () => import("../views/Maintenance.vue")
  },
  {
    path: "*",
    component: () => import("../views/ErrorPage.vue"),
    props: { errorMessage: "pageNotFound", errorCode: "404" }
  },
  {
    path: "/spacegraph/viewer/:layoutId",
    name: "SpaceGraphViewer",
    props: true,
    component: () => import("../views/SpaceGraphViewerPage.vue"),
  },
  {
    path: "/auth/callback",
    name: "Callback",
    component: () => import("../views/auth/Callback.vue")
  },
  {
    path: "/tkpaylogin",
    name: "TkpayLogin",
    meta: { noSidebar: true },
    component: () => import("../views/auth/TkpayLogin.vue")
    // Tkpay testlerinden sonra kaldırılacak
  },
];

const environmentRoutes = [];
if (process.env.VUE_APP_ENVIRONMENT !== "Jandarma") {
  environmentRoutes.push({
    path: "/graphQuery",
    name: "GraphQuery",
    component: () => import("../views/GraphQuery.vue")
  });
}

otherRoutes = [...otherRoutes, ...environmentRoutes];

const router = new VueRouter({
  routes: otherRoutes
});

const addApplicationModeRoutes = (applicationMode) => {
  const tempRouter = new VueRouter({
    routes: otherRoutes
  });
  router.matcher = tempRouter.matcher;
  switch (applicationMode) {
    case constant.APPLICATIONMODE.AML:
      router.addRoutes([...amlRoutes, ...aksitRoutes, ...examRoutes, ...faceRoutes, ...tutorialRoutes, ...cddRoutes]);
      break;
    case constant.APPLICATIONMODE.FRAUD:
      const homeRedirect = {
        path: "/",
        name: "Home",
        component: () => import("../components/fraud/FraudManagementBase.component.vue"),
        props: {
          managementComponent: () => import("../views/fraud/FraudIncidentManagement.vue"),
          isServiceEntityRequired: true
        },
        meta: {
          layout: () => import("../components/layouts/FraudLayout.component.vue"),
          icon: "mdi-calendar",
          localize: "fraud.incidents.title",
          isEntitySelectionEnabled: true
        }
      };

      router.addRoutes([homeRedirect, ...fraudRoutes, ...tutorialRoutes, ...cddRoutes]);
      break;
    case constant.APPLICATIONMODE.MIXED:
      router.addRoutes([...amlRoutes, ...aksitRoutes, ...fraudRoutes, ...examRoutes, ...faceRoutes, ...tutorialRoutes, ...cddRoutes]);
      break;
  }
};

if (localStorage.clientSettings) {
  const clientSettings = JSON.parse(localStorage.clientSettings);
  const applicationModeInt = parseInt(clientSettings.ApplicationMode);
  addApplicationModeRoutes(applicationModeInt);
}

router.beforeEach((to, from, next) => {
  // Bakım kontrolü yalnızca Maintenance routeuna girişte yapılır; normal sayfalarda
  // bakıma geçiş 503 interceptorü tarafından yönetilir. Sonuç MaintenanceService
  // üzerinden cachelendiği için her navigasyonda yeni istek atılmaz.
  if (to.name === 'Maintenance') {
    maintenanceService.getStatus()
      .then(status => {
        if (status && status.enabled) {
          next();
        } else {
          next({ path: '/' });
        }
      })
      .catch(error => {
        console.error('Bakım durumu kontrol edilemedi:', error);
        // Sorgunun kendisi 503 dönüyorsa sunucu zaten hizmet veremiyor demektir; kullanıcıyı
        // ana sayfaya geri atmak 503 -> yönlendirme -> geri atma döngüsü oluşturur.
        const status = error && (error.status || (error.response && error.response.status));
        if (status === 503) {
          next();
        } else {
          next({ path: '/' });
        }
      });
    return;
  }
  // Cachede bakımın açık olduğu biliniyorsa istek atmadan yönlendirilir.
  const cachedStatus = maintenanceService.getCachedStatus();
  if (cachedStatus && cachedStatus.enabled) {
    next({ name: 'Maintenance' });
    return;
  }
  next();
});

// Eslesen route kayitlarinin (parent + child) meta.permission degerleri AND, tek bir
// meta.permission icindeki virgulle ayrilmis isimler OR ile degerlendirilir (v-perm ile ayni).
const hasRoutePermission = (route) => {
  const requiredPermissions = route.matched
    .map((record) => record.meta && record.meta.permission)
    .filter((permission) => !!permission);

  return requiredPermissions.every((permission) =>
    permission.split(",").some((name) => commonFunc.checkPermissionWithName(name.trim()))
  );
};

router.beforeEach(async (to, from, next) => {
  const token = localStorage.access_token;
  if (to.path !== from.path) {
    globalLoadingState.isLoading = true;
  }
  // Login sayfasına giderken OidcLoginOnly değerini çek
  if (to.name === 'Login' && !localStorage.getItem('OidcLoginOnly')) {
    try {
      const response = await apicaller.get(api.AUTH.getOidcLoginOnly);
      if (response.data) {
        localStorage.setItem('OidcLoginOnly', String(response.data.oidcLoginOnly));
      }
    } catch (e) {
      console.log('Failed to fetch OidcLoginOnly:', e);
    }
  }

  if (localStorage.applicationMode == constant.APPLICATIONMODE.MIXED && to.name == "Home" && localStorage.selectedApp == constant.APPLICATIONMODE.FRAUD) {
    next({ name: 'IncidentManagements' });
  }
  // TODO ktolga: Tkpay testlerinden sonra tkpaylogin kaldırılacak
  else if (to.name !== 'Login' && to.name !== 'TkpayLogin' && to.name !== 'GraphQuery' && to.name !== 'Maintenance' && to.path !== '/auth/callback' && (!token)) {
    const preferredLanguage = localStorage.getItem("preferredLanguage");
    // zc: Giriş yapılmadan erişilmek istenen adresi sakladık başarılı login sonrası bu adrese yönlendirilecek
    const redirectAfterLogin = (to.fullPath && to.fullPath !== '/' && to.path !== '/login') ? to.fullPath : null;
    const oidcLoginOnly = localStorage.getItem("OidcLoginOnly");
    localStorage.clear();
    localStorage.setItem("preferredLanguage", preferredLanguage);
    if (redirectAfterLogin) {
      localStorage.setItem("redirectAfterLogin", redirectAfterLogin);
    }
    if (oidcLoginOnly !== null) localStorage.setItem("OidcLoginOnly", oidcLoginOnly);
    store.dispatch('commonStates/clearRouteHistory');
    next({ name: 'Login' });
  } else if (token && to.name === 'Login') {
    next({ name: 'Home' });
  }
  else if (!hasRoutePermission(to)) {
    next({ name: 'Forbidden' });
  }
  else {
    // todo: duygu from.name == null ise sayfa refreshi ya da sayfanın ilk açılması
    // signalR sayfa açık değilken bildirimi arttıramadığı için bildiirmlerim sayısının veritabanından çekilip localstorage yazılması gerekmekte
    // if the user is not authenticated, `next` is called twice
    next();
  }
})

/* 
  This function was added to manage the loading state after each route change
  After logging-in, due js files are not cached; it loads them and loading does not appears for a while, rel: bug/11371
  TODO: Applying g-zip operation of these files might increase loading of this files dramatically
*/
router.afterEach((to, from) => {
  requestAnimationFrame(() => {
    globalLoadingState.isLoading = false;
  });

  clearAllToasts();

  // ŞİB Takibi kalan gün sayısı: değişiminde çekilir.
  // from.name == null → ilk açılış / refresh (Chrome kapatılıp yeniden girildiğinde de bu dal çalışır).
  if (localStorage.access_token && to.name !== "Login" && (from.name == null || to.path !== from.path)) {
    store.dispatch("commonStates/fetchSibTrackingRemainingDay");
  }
});

export { addApplicationModeRoutes };

export default router;
